import { toSignal } from '@angular/core/rxjs-interop';
import { computed, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { SelectionService } from '@portal/app/shared/services/selection.service';
import { AuthService } from '@portal/app/shared/services/auth.service';

import {
  FeatureConfigV2ControllerService,
  FeatureConfigResponseDTO
} from '@libs/api';

@Injectable({
  providedIn: 'root'
})
export class FeatureConfigService {
  private dataSubject = new BehaviorSubject<FeatureConfigResponseDTO[]>([]);

  data$ = this.dataSubject.asObservable();
  private dataSignal = toSignal(this.data$, { initialValue: [] });

  constructor(
    private readonly featureConfigV2ControllerService: FeatureConfigV2ControllerService,
    private readonly selectionService: SelectionService,
    private readonly authService: AuthService
  ) {
    this.fetchData();
  }

  fetchData() {
    const { clientId, brandId } = this.selectionService.getClientIdAndBrandId();
    return this.featureConfigV2ControllerService
      .getAllFeatureConfigs(clientId, brandId)
      .subscribe((data: FeatureConfigResponseDTO[]) => {
        this.setData(data);
      });
  }

  setData(data: FeatureConfigResponseDTO[]): void {
    this.dataSubject.next(data);
  }

  getData(): FeatureConfigResponseDTO[] {
    return this.dataSubject.getValue();
  }

  private checkFeatureEnabled(
    items: FeatureConfigResponseDTO[],
    key: FeatureConfigResponseDTO.FeatureNameEnum
  ): boolean {
    const isMeasuredUser = this.authService.getUser()?.isMeasuredUser();
    const visibilityOptions = isMeasuredUser
      ? [
          FeatureConfigResponseDTO.VisibilityEnum.InternalUsers,
          FeatureConfigResponseDTO.VisibilityEnum.AllUsers,
          FeatureConfigResponseDTO.VisibilityEnum.InternalCsUsers
        ]
      : [FeatureConfigResponseDTO.VisibilityEnum.AllUsers];
    return items.some(
      (item) =>
        item.featureName === key &&
        visibilityOptions.includes(
          item.visibility as FeatureConfigResponseDTO.VisibilityEnum
        )
    );
  }

  isFeatureEnabled(key: FeatureConfigResponseDTO.FeatureNameEnum): boolean {
    return this.checkFeatureEnabled(this.dataSubject.getValue(), key);
  }

  isFeatureEnabledSignal(key: FeatureConfigResponseDTO.FeatureNameEnum) {
    return computed(() => this.checkFeatureEnabled(this.dataSignal(), key));
  }

  /**
   * Gets the visibility level for a specific feature
   * @param key The feature name enum
   * @returns The visibility enum value if feature exists, undefined otherwise
   */
  getFeatureVisibility(
    key: FeatureConfigResponseDTO.FeatureNameEnum
  ): FeatureConfigResponseDTO.VisibilityEnum | undefined {
    const featureConfig = this.dataSubject
      .getValue()
      .find((item) => item.featureName === key);

    return featureConfig?.visibility as FeatureConfigResponseDTO.VisibilityEnum;
  }
}
